/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { Calculator, GitBranch, ReceiptText } from 'lucide-react';
import { chamarApi } from '@/nucleo/auth/cliente-api';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';
const NS = '/bff/v1/j08-s04-consolidar-medicao';
const moeda = (v: any) => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
export function ConsolidarMedicaoPage() {
  const [s, setS] = useState<any>(),
    [busy, setBusy] = useState(false),
    [tipo, setTipo] = useState('GLOSA'),
    [valor, setValor] = useState(''),
    [motivo, setMotivo] = useState('');
  async function call(path: string, body?: any) {
    setBusy(true);
    try {
      const e = await chamarApi<any>(NS + path, {
        metodo: path === '/state' ? 'GET' : 'POST',
        corpo: body,
      });
      setS(e.state);
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    void call('/state');
  }, []);
  if (!s)
    return (
      <main role="status" className="p-6">
        Carregando medições…
      </main>
    );
  const c = s.consolidacoes[0],
    v = c?.versaoAtual;
  return (
    <main className="min-w-0 max-w-[calc(100vw-2rem)] space-y-5 overflow-hidden">
      <header>
        <h1 className="text-3xl font-semibold">
          <Calculator className="mr-2 inline h-7 w-7" />
          {s.titulo}
        </h1>
        <p>{s.descricao}</p>
        <p className="text-sm text-muted-foreground">
          Sessão: {s.sessao?.nome} · {s.sessao?.email}
        </p>
      </header>
      {s.alertas.map((a: any, i: number) => (
        <div role="alert" className="rounded-md border border-destructive p-3" key={i}>
          {a.mensagem} <span className="font-mono text-xs">{a.codigo}</span>
        </div>
      ))}
      {!c ? (
        <section className="space-y-3 rounded-md border border-dashed p-5">
          <p>
            Há {s.competenciasSeladas.filter((x: any) => !x.consolidada).length} competência selada
            disponível.
          </p>
          {s.competenciasSeladas
            .filter((x: any) => !x.consolidada)
            .map((x: any) => (
              <Button
                key={x.id}
                disabled={busy}
                onClick={() =>
                  void call('/verbs/gerar_medicao', {
                    selamentoId: x.id,
                    chaveIdempotencia: crypto.randomUUID(),
                  })
                }
              >
                Gerar medição de {x.snapshot.entradas.competencia}
              </Button>
            ))}
        </section>
      ) : (
        <>
          <section className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-md border p-4">
              <p className="text-sm text-muted-foreground">Credenciado</p>
              <strong>{c.credenciadoNome}</strong>
            </div>
            <div className="rounded-md border p-4">
              <p className="text-sm text-muted-foreground">Versão e estado</p>
              <strong>
                v{c.versao_atual} · {c.estado}
              </strong>
              <Button
                className="mt-2 w-full"
                variant="outline"
                disabled={busy}
                onClick={() =>
                  void call('/verbs/gerar_medicao', {
                    selamentoId: c.selamento_id,
                    chaveIdempotencia: crypto.randomUUID(),
                  })
                }
              >
                Reexecutar consolidação
              </Button>
            </div>
            <div className="rounded-md border p-4">
              <p className="text-sm text-muted-foreground">Líquido</p>
              <strong>{moeda(v?.liquido)}</strong>
            </div>
          </section>
          <section className="space-y-3 rounded-md border p-4">
            <h2 className="font-semibold">
              <ReceiptText className="mr-2 inline h-5 w-5" />
              Demonstrativo preliminar
            </h2>
            <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-5">
              <span>
                Subtotal
                <br />
                <strong>{moeda(v?.subtotal)}</strong>
              </span>
              <span>
                Ajustes
                <br />
                <strong>{moeda(v?.ajustes)}</strong>
              </span>
              <span>
                Glosas
                <br />
                <strong>{moeda(v?.glosas)}</strong>
              </span>
              <span>
                Retenções
                <br />
                <strong>{moeda(v?.retencoes)}</strong>
              </span>
              <span>
                Bruto
                <br />
                <strong>{moeda(v?.bruto)}</strong>
              </span>
            </div>
            <p className="break-all font-mono text-xs">Hash {v?.hash}</p>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => void call('/verbs/recalcular_totais', { consolidacaoId: c.id })}
            >
              Recalcular e conferir soma
            </Button>
            {s.resultado?.coerente !== undefined && (
              <p role="status">
                Recalculado: {moeda(s.resultado.recalculado)} ·{' '}
                {s.resultado.coerente ? 'coerente' : 'divergente'}
              </p>
            )}
          </section>
          <section className="space-y-3 rounded-md border p-4">
            <h2 className="font-semibold">Ajuste ou glosa manual</h2>
            <div className="grid gap-2 sm:grid-cols-3">
              <label>
                Tipo
                <select
                  className="block h-10 w-full rounded-md border bg-background px-3"
                  value={tipo}
                  onChange={(e) => setTipo(e.target.value)}
                >
                  <option>GLOSA</option>
                  <option>AJUSTE</option>
                </select>
              </label>
              <label htmlFor="medicao-valor">
                Valor
                <Input
                  id="medicao-valor"
                  inputMode="decimal"
                  value={valor}
                  onChange={(e) => setValor(e.target.value)}
                />
              </label>
              <label htmlFor="medicao-motivo">
                Motivo
                <Input
                  id="medicao-motivo"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                />
              </label>
            </div>
            <Button
              disabled={busy || !valor}
              onClick={() =>
                void call('/verbs/solicitar_ajuste', {
                  consolidacaoId: c.id,
                  tipo,
                  valor: Number(valor),
                  motivo,
                  chaveIdempotencia: crypto.randomUUID(),
                })
              }
            >
              Solicitar aprovação
            </Button>
            {c.ajustes.map((a: any) => (
              <article key={a.id} className="rounded-md bg-muted p-3">
                <p>
                  {a.tipo} {moeda(a.valor)} · {a.estado}
                </p>
                <p className="text-sm">
                  {a.motivo} — solicitado por {a.solicitadoPor}
                </p>
                {a.estado === 'PENDENTE' && (
                  <div className="mt-2 flex gap-2">
                    <Button
                      disabled={busy}
                      onClick={() =>
                        void call('/verbs/decidir_ajuste', {
                          ajusteId: a.id,
                          decisao: 'APROVADO',
                          motivo: 'Aprovado após conferência documental.',
                          chaveIdempotencia: crypto.randomUUID(),
                        })
                      }
                    >
                      Aprovar
                    </Button>
                    <Button
                      variant="outline"
                      disabled={busy}
                      onClick={() =>
                        void call('/verbs/decidir_ajuste', {
                          ajusteId: a.id,
                          decisao: 'REJEITADO',
                          motivo: 'Rejeitado após conferência documental.',
                          chaveIdempotencia: crypto.randomUUID(),
                        })
                      }
                    >
                      Rejeitar
                    </Button>
                  </div>
                )}
              </article>
            ))}
          </section>
          <section className="space-y-2 rounded-md border p-4">
            <h2 className="font-semibold">
              <GitBranch className="mr-2 inline h-5 w-5" />
              Rastreabilidade até a origem
            </h2>
            {c.itens.map((i: any) => (
              <div key={i.id} className="wrap-break-word text-sm">
                <strong>{i.protocolo}</strong> · OS {i.origem.ordemServicoId} · conjunto{' '}
                {i.origem.conjuntoId} · composição {i.origem.composicaoId} · selo{' '}
                {i.origem.selamentoId}
              </div>
            ))}
          </section>
        </>
      )}
    </main>
  );
}
